import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/pages/Login";
import { ArrowRight, MailCheck } from "lucide-react";
import { toast } from "sonner";

const IMG = "https://images.unsplash.com/photo-1517456793572-1d8efd6dc135";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { register, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("member");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(name, email, password, role);
    setLoading(false);
    if (res.ok && res.pending) { setPending(true); return; }
    if (res.ok) { toast.success("Welcome to the movement."); navigate(res.user?.role === "supporter" ? "/supporter" : "/dashboard"); }
    else toast.error(res.error);
  };

  const resend = async () => {
    setResending(true);
    const res = await resendVerification(email);
    setResending(false);
    if (res.ok) toast.success("Verification email sent. Check your inbox.");
    else toast.error(res.error);
  };

  if (pending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone px-6" data-testid="verify-pending">
        <div className="w-full max-w-md text-center">
          <Link to="/" className="block"><img src="/logo.png" alt="In His Image Movement Ministries" className="h-24 w-auto object-contain mx-auto" /></Link>
          <div className="mt-10 inline-flex items-center justify-center w-16 h-16 border border-navy/20 bg-navy/5 rounded-full">
            <MailCheck size={26} className="text-navy" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif font-light text-4xl mt-8 tracking-tight">Check your inbox.</h1>
          <p className="font-sans text-charcoal/60 mt-4">
            We've sent a verification link to <span className="text-charcoal font-medium">{email}</span>.
            Click it to activate your account, then sign in.
          </p>
          {role === "supporter" && (
            <p className="font-sans text-sm text-charcoal/60 mt-4 border-t border-charcoal/10 pt-4">
              Once verified, you can make a gift to unlock your Supporter status and perks.
            </p>
          )}
          <button onClick={resend} disabled={resending} data-testid="resend-verification-btn"
            className="mt-8 font-mono text-xs uppercase tracking-widest text-olive border-b border-olive/40 hover:text-charcoal transition-colors disabled:opacity-50">
            {resending ? "Sending..." : "Resend verification email"}
          </button>
          <p className="font-sans text-sm text-charcoal/60 mt-8">
            Already verified? <Link to="/login" className="text-olive border-b border-olive/40">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-bone">
      <div className="flex items-center justify-center px-6 py-24 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="block"><img src="/logo.png" alt="In His Image Movement Ministries" className="h-24 w-auto object-contain mx-auto" /></Link>
          <h1 className="font-serif font-light text-5xl mt-10 tracking-tight text-center">Join us.</h1>
          <p className="font-sans text-charcoal/60 mt-3 text-center">Become part of a brotherhood built in His image.</p>

          <button onClick={googleLogin} data-testid="google-register-btn"
            className="w-full mt-10 border border-charcoal/20 py-3.5 font-mono text-xs uppercase tracking-widest hover:bg-sand transition-colors flex items-center justify-center gap-3">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
          </button>
          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-charcoal/15" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">or</span>
            <div className="flex-1 h-px bg-charcoal/15" />
          </div>

          <form onSubmit={submit} className="space-y-6">
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60 mb-3 block">I want to join as</label>
              <div className="grid grid-cols-2 gap-3">
                {[{ k: "member", t: "Member", d: "Grow & belong" }, { k: "supporter", t: "Supporter", d: "Give & partner" }].map((o) => (
                  <button type="button" key={o.k} onClick={() => setRole(o.k)} data-testid={`role-${o.k}`}
                    className={`text-left p-4 border transition-colors ${role === o.k ? "border-navy bg-navy text-bone" : "border-charcoal/20 hover:border-charcoal"}`}>
                    <span className="font-serif text-xl block">{o.t}</span>
                    <span className={`font-mono text-[10px] uppercase tracking-widest ${role === o.k ? "text-bone/70" : "text-charcoal/50"}`}>{o.d}</span>
                  </button>
                ))}
              </div>
            </div>
            <Input label="Full Name" value={name} onChange={setName} testid="register-name" />
            <Input label="Email" type="email" value={email} onChange={setEmail} testid="register-email" />
            <Input label="Password" type="password" value={password} onChange={setPassword} testid="register-password" />
            <button type="submit" disabled={loading} data-testid="register-submit"
              className="group w-full bg-charcoal text-bone py-4 font-mono text-xs uppercase tracking-widest hover:bg-olive transition-colors flex items-center justify-center gap-3 disabled:opacity-50">
              {loading ? "Creating..." : "Create Account"} <ArrowRight size={16} className="hover-arrow" />
            </button>
          </form>
          <p className="font-sans text-sm text-charcoal/60 mt-8">
            Already a member? <Link to="/login" className="text-olive border-b border-olive/40">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/40" />
        <div className="absolute bottom-16 left-12 text-bone max-w-md">
          <p className="font-serif text-5xl italic leading-tight">"As iron sharpens iron."</p>
          <span className="font-mono text-xs uppercase tracking-widest text-bone/70 mt-4 block">Proverbs 27:17</span>
        </div>
      </div>
    </div>
  );
}
